import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Mail, Search, RefreshCw, Download, ExternalLink, Pencil,
  Plane, ArrowRight, CheckCircle2, XCircle, Clock, X
} from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getRemainingText(expiresIso) {
  if (!expiresIso) return "";
  const diff = new Date(expiresIso) - new Date();
  if (diff <= 0) return "Expired";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h remaining`;
  return `${h}h ${m}m remaining`;
}

function EditModal({ order, onClose, onSaved }) {
  const { t } = useTranslation();
  const initPassengers = order.passengers?.length
    ? order.passengers.map(p => ({ title: p.title || "Mr", firstName: p.first_name, lastName: p.last_name }))
    : [{ title: order.passenger_title || "Mr", firstName: order.passenger_first_name, lastName: order.passenger_last_name }];
  const [passengers, setPassengers] = useState(initPassengers);
  const [saving, setSaving] = useState(false);

  const updatePax = (idx, field, val) => setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));

  const handleSave = async () => {
    for (const p of passengers) {
      if (p.firstName.trim().length < 2 || p.lastName.trim().length < 2) {
        toast.error(t("myorders.edit_error_name"));
        return;
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${order.booking_reference}/edit`, {
        passengers: passengers.map(p => ({ title: p.title, first_name: p.firstName.trim(), last_name: p.lastName.trim() })),
      });
      toast.success(t("myorders.edit_success"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("myorders.edit_error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f]">
            {t("myorders.edit_title")} {order.booking_reference}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-[#f8fafc] rounded-lg">
            <X className="w-5 h-5 text-[#6b7280]" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          {passengers.map((pax, idx) => (
            <div key={idx} className={idx > 0 ? "border-t border-[#e2e8f0] pt-4" : ""}>
              <p className="text-xs font-semibold text-[#6b7280] uppercase mb-2">Passenger {idx + 1}</p>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <select value={pax.title} onChange={e => updatePax(idx, "title", e.target.value)}
                  className="border border-[#e2e8f0] rounded-xl px-2 py-2.5 text-sm bg-[#f8fafc]">
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                </select>
                <input type="text" value={pax.firstName} onChange={e => updatePax(idx, "firstName", e.target.value)}
                  className="border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm bg-[#f8fafc]"
                  placeholder="First" data-testid={`edit-first-name-${idx}`} />
                <input type="text" value={pax.lastName} onChange={e => updatePax(idx, "lastName", e.target.value)}
                  className="border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm bg-[#f8fafc]"
                  placeholder="Last" data-testid={`edit-last-name-${idx}`} />
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-[#6b7280] mb-4">{t("myorders.edit_note")}</p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-[#e2e8f0] text-[#6b7280] py-2.5 rounded-xl text-sm font-medium hover:bg-[#f8fafc] transition-colors"
            data-testid="edit-cancel-btn"
          >
            {t("myorders.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#f97316] text-white py-2.5 rounded-xl text-sm font-medium hover:bg-[#ea580c] transition-colors disabled:opacity-60"
            data-testid="edit-save-btn"
          >
            {saving ? "..." : t("myorders.save_changes")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function OrderCard({ order, onEdit }) {
  const { t } = useTranslation();
  const fd = order.flight_data || {};
  const isExpired = order.status === "expired";
  const remaining = getRemainingText(order.validity_expires_at_iso);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#e2e8f0] rounded-2xl p-5 hover:shadow-sm transition-shadow"
      data-testid={`order-card-${order.booking_reference}`}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[#6b7280]">Ref:</span>
          <span className="font-['Outfit'] font-bold text-[#1e3a5f] tracking-wider">{order.booking_reference}</span>
        </div>
        <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
          isExpired ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-600 border border-green-200"
        }`}>
          {isExpired ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
          {isExpired ? "EXPIRED" : "CONFIRMED"}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-[#1f2937] mb-2">
        <span className="font-semibold">{fd.departure_code}</span>
        <ArrowRight className="w-3 h-3 text-[#6b7280]" />
        <span className="font-semibold">{fd.arrival_code}</span>
        <span className="text-[#6b7280]">&middot;</span>
        <span className="text-[#6b7280]">{fd.departure_date}</span>
        <span className="text-[#6b7280]">&middot;</span>
        <span className="text-[#6b7280]">{fd.airline} {fd.flight_number}</span>
      </div>

      <p className="text-sm text-[#1f2937] mb-2">
        {order.passenger_title} {order.passenger_first_name} {order.passenger_last_name}
        {order.passengers?.length > 1 && <span className="text-[#6b7280] text-xs ml-1">+{order.passengers.length - 1} other{order.passengers.length > 2 ? "s" : ""}</span>}
      </p>

      <div className="flex items-center gap-1 text-xs mb-4">
        <Clock className={`w-3 h-3 ${isExpired ? "text-red-400" : "text-[#f97316]"}`} />
        <span className={isExpired ? "text-red-500" : "text-[#6b7280]"}>
          {isExpired ? `Expired: ${order.validity_expires_at}` : remaining}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <a
          href={`${API}/orders/${order.booking_reference}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-medium text-[#1e3a5f] border border-[#e2e8f0] px-3 py-1.5 rounded-lg hover:bg-[#f8fafc] transition-colors"
          data-testid={`download-pdf-${order.booking_reference}`}
        >
          <Download className="w-3 h-3" /> {t("success.download_pdf")}
        </a>
        <a
          href={`/verify/${order.booking_reference}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-medium text-[#1e3a5f] border border-[#e2e8f0] px-3 py-1.5 rounded-lg hover:bg-[#f8fafc] transition-colors"
          data-testid={`verify-link-${order.booking_reference}`}
        >
          <ExternalLink className="w-3 h-3" /> {t("myorders.verify_link")}
        </a>
        {!isExpired && (
          <button
            onClick={() => onEdit(order)}
            className="flex items-center gap-1.5 text-xs font-medium text-[#f97316] border border-[#f97316]/30 px-3 py-1.5 rounded-lg hover:bg-[#fff7ed] transition-colors"
            data-testid={`edit-btn-${order.booking_reference}`}
          >
            <Pencil className="w-3 h-3" /> {t("myorders.edit")}
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function MyOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchedEmail, setSearchedEmail] = useState("");
  const [editOrder, setEditOrder] = useState(null);

  const fetchOrders = async (e) => {
    if (e) e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("myorders.error_email"));
      return;
    }
    setLoading(true);
    setSearchedEmail(email.trim());
    try {
      const res = await axios.get(`${API}/orders/my-orders`, { params: { email: email.trim() } });
      setOrders(res.data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (searchedEmail) {
      setEmail(searchedEmail);
      fetchOrders();
    }
  };

  const handleEditSaved = () => {
    setEditOrder(null);
    handleRefresh();
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="my-orders-page">
      <SEOHead title="My Orders | OnwardFlighTickets" description="View and manage your onward flight reservations." path="/my-orders" />
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-2">
              {t("myorders.title")}
            </h1>
            <p className="text-[#6b7280]">{t("myorders.subtitle")}</p>
          </motion.div>

          {/* Email lookup */}
          <motion.form
            onSubmit={fetchOrders}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-8"
          >
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("myorders.email_placeholder")}
                  className="w-full border border-[#e2e8f0] rounded-xl pl-10 pr-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none"
                  data-testid="myorders-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#f97316] text-white px-6 py-3 rounded-xl font-medium text-sm hover:bg-[#ea580c] transition-all flex items-center gap-2 disabled:opacity-60"
                data-testid="myorders-search-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {t("myorders.view_orders")}
              </button>
            </div>
          </motion.form>

          {/* Results */}
          {orders !== null && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#6b7280]">
                  {t("myorders.orders_for")} <span className="font-medium text-[#1f2937]">{searchedEmail}</span>
                </p>
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-1 text-xs text-[#f97316] font-medium hover:underline"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-3 h-3" /> {t("myorders.refresh")}
                </button>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="border border-[#e2e8f0] rounded-2xl p-5">
                      <Skeleton className="h-4 w-32 mb-3" />
                      <Skeleton className="h-4 w-48 mb-2" />
                      <Skeleton className="h-4 w-40" />
                    </div>
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <Plane className="w-12 h-12 text-[#d1d5db] mx-auto mb-3" />
                  <p className="text-[#6b7280] mb-4">{t("myorders.no_orders")}</p>
                  <button
                    onClick={() => navigate("/")}
                    className="bg-[#f97316] text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-[#ea580c] transition-all"
                    data-testid="book-first-btn"
                  >
                    {t("myorders.book_first")}
                  </button>
                </motion.div>
              ) : (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map((order) => (
                    <OrderCard
                      key={order.booking_reference}
                      order={order}
                      onEdit={setEditOrder}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Edit Modal */}
      <AnimatePresence>
        {editOrder && (
          <EditModal
            order={editOrder}
            onClose={() => setEditOrder(null)}
            onSaved={handleEditSaved}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
